import * as CoreModels from '../models/CoreModels'
import { ModuleTypeInfo, OnChangeCallback } from './UtilityTypes';
import StatusCode from './StatusCode';
import { CoreUserListing } from '../models/CoreUser';
import { FlowSyncProduct, PageResult, PaginationResult } from '../models/CoreModels';
import { CacheRequestState, RequestState } from '../models/RequestState';
import { CoreActivityListing } from '../models/CoreActivity';
import i18n from 'i18next';

// --- Functions ---
export const formatDate = (formatDate?: string, includeSeconds: boolean = true): string => formatDate ? new Date(formatDate).toLocaleString(i18n.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined
}) : '-';

export const formatDateOnly = (formatDate?: string): string => formatDate ? new Date(formatDate).toLocaleDateString(i18n.language) : '-';

export const active = (active: boolean): string => active ? i18n.t('yes', { ns: 'common' }) : i18n.t('no', { ns: 'common' });

export const activeText = (active: boolean | undefined): string => (active !== undefined) ?
    active ? i18n.t('status.active', { ns: 'common' }) : i18n.t('status.inactive', { ns: 'common' })
    : '-';

export const getISODate = (date: Date) => date.toLocaleDateString(i18n.language, { year: 'numeric', month: "2-digit", day: '2-digit' });

export const update = <T>(name: string, source: T, change: Partial<T>, callback: OnChangeCallback<T>) => callback(name, {
    ...source,
    ...change
});

//Activity
export const presentStudentGroups = (activity: CoreActivityListing): string => activity.groupAssignments?.map(ga => ga.title).join(', ') ?? 'Saknas';

//User
export const presentRole = (user: CoreUserListing): Array<{ type: string, id: number }> => {

    const roles: Array<{ type: string, id: number }> = []

    if (user.enrollments && user.enrollments.length) {
        roles.push({ type: 'studentTypes', id: 1 });        
    }

    if (user.placements && user.placements.length) {
        roles.push({ type: 'placementChildTypes', id: 1 });
    }

    if (user.duties) {
        for (var i = 0; i < user.duties.length; i++) {
            var id = user.duties[i]?.dutyRole?.id;
            if (id !== undefined) {
                roles.push({ type: 'dutyRoleTypes', id: id })
            }
        }
    }

    if (user.userRelations) {
        for (var j = 0; j < user.userRelations.length; j++) {
            roles.push({ type: 'parentRelationTypes', id: user.userRelations[j].relationType })
        }
    }

    let distinctRoles = roles.filter(
        (obj, index, arr) => {
            return arr.findIndex(o => {
                return o.id === obj.id && o.type === obj.type
            }) === index
        }
    );

    return distinctRoles;
};

export const copyToClipboard = (value: string) => {
    var dummy = document.createElement("input");
    document.body.appendChild(dummy);
    dummy.setAttribute("id", "clipboard_dummy");
    const clipboard = getElementById<HTMLInputElement>("clipboard_dummy");
    if (clipboard) {
        clipboard.value = value;
    }
    dummy.select();
    document.execCommand("copy");
    document.body.removeChild(dummy);
}

export const equvivalent = <T>(a: T, b: T): boolean => {
    const type = typeof (a);

    // Is type equal and handle special cases.
    if (type !== typeof (b)) {
        return false;
    } else if (type === 'number') {
        return a === b || (a !== a && b !== b);
    } else if (type !== 'object') {
        return a === b;
    } else if (a === null) {
        return b === null;
    } else if (b === null) {
        return false;
    }

    // If array check if items are equal, order is not considered.
    if (Array.isArray(a)) {
        if (Array.isArray(b)) {
            if (a.length !== b.length) {
                return false;
            }

            const indiciesB: number[] = [];
            a.forEach((itemA) => {
                const indexB = b.findIndex((itemB: any, i: number) => !indiciesB.some(x => x === i) && equvivalent(itemA, itemB));
                if (indexB !== -1) {
                    indiciesB.push(indexB);
                }
            })

            return indiciesB.length === a.length;
        }

        return false;
    }

    if (typeof a === 'object' && typeof b === 'object' && equvivalent(Object.keys(a), Object.keys(b))) {
        return Object.keys(a).every(key => equvivalent(a[key as keyof T], b[key as keyof T]))
    }

    return false;
}

export const formatText = (raw?: string) => {
    if (!raw) {
        return null;
    }

    try {
        const json = JSON.parse(raw);
        if (typeof json === "object") {
            return JSON.stringify(json, null, 2)
        }
    }
    catch (e) {
        return raw;
    }
}

const getElementById = <TElement extends HTMLElement>(elementId: string): TElement | null => {
    var element = document.getElementById(elementId);
    if (element) {
        return element as TElement;
    }

    return null;
}

export const distinctSchoolAdminModuleTypes = (moduleTypes: RequestState<CoreModels.CoreModule[]>): ModuleTypeInfo[] => {
    if (moduleTypes.code === StatusCode.COMPLETE) {
        return moduleTypes.data
            .filter(x => x.product === FlowSyncProduct.SCHOOL_ADMIN)
            .filter((value, index, self) => self.findIndex(m => m.moduleTypeName === value.moduleTypeName) === index)
            .map(m => ({
                moduleType: m.moduleType,
                moduleTypeDisplayName: m.moduleTypeDisplayName,
                moduleTypeName: m.moduleTypeName
            }));
    }
    return [];
};

export const getModuleTypeObj = (modules: ModuleTypeInfo[], moduleType: string) => {
    return modules.find(m => m.moduleTypeName === moduleType);
}

export const ensureArray = <T>(array?: T[]): T[] => {
    if (array) {
        return array;
    }

    return new Array<T>(0);
}

/**
 * Should be migrated to using RequestState<PageResult<T>> directly.
 * @param paginationResult
 * @returns
 */
export const convertToCacheRequestState = <T>(paginationResult: PaginationResult<T[]>): CacheRequestState<PageResult<T>> => {
    switch (paginationResult.code) {
        case StatusCode.COMPLETE:
            return {
                code: StatusCode.COMPLETE,
                data: {
                    currentPage: paginationResult.pagination.currentPage,
                    totalCount: paginationResult.totalCount ?? 0,
                    pageSize: paginationResult.pagination.pageSize,
                    totalPages: paginationResult.pagination.totalPages,
                    values: paginationResult.data
                }
            };
        case StatusCode.ERROR:
            return {
                code: StatusCode.ERROR,
                error: {
                    ...paginationResult.error,
                    isAxiosError: false,
                    toJSON: () => ({}),
                }
            };
        case StatusCode.PENDING:
        case StatusCode.NONE:
            return {
                code: paginationResult.code
            };
    }
}

export const getTotalCount = <T>(requestState?: CacheRequestState<PageResult<T>>): number | undefined => {
    if (requestState === undefined || requestState.code === StatusCode.NONE || requestState.code === StatusCode.ERROR) {
        return undefined;
    }

    return requestState.data?.totalCount;
}

export const getTotalCountText = <T>(requestState: CacheRequestState<PageResult<T>>): string => {
    const count = getTotalCount(requestState);

    if (count === undefined) {
        return '';
    }
    return `(${count})`
}

export const distinctBy = <T, TKey extends number | string>(array: T[], keySelector: (item: T) => TKey): T[] => {
    const usedKeys = new Set<TKey>();
    const result: T[] = [];

    for (let i = 0; i < array.length; i++) {
        const key = keySelector(array[i]);
        if (!usedKeys.has(key)) {
            result.push(array[i]);
            usedKeys.add(key);
        }
    }

    return result;
}

export const compareStrings = (a: string, b: string): number => {
    if (a === b) {
        return 0;
    } else if (a < b) {
        return -1;
    } else {
        return 1;
    }
}

export const convertRequestState = <TSource, TResult>(source: RequestState<TSource>, converter: (sourceData: TSource) => TResult): RequestState<TResult> => {
    switch (source.code) {
        case StatusCode.NONE:
            return {
                code: StatusCode.NONE,
                key: source.key,
            }
        case StatusCode.PENDING:
            return {
                code: StatusCode.PENDING,
                key: source.key,
            }
        case StatusCode.COMPLETE:
            return {
                code: StatusCode.COMPLETE,
                key: source.key,
                data: converter(source.data),
            }
        case StatusCode.ERROR:
            return {
                code: StatusCode.ERROR,
                key: source.key,
                error: source.error,
            }
    }
}

export const getDataOrDefault = <TData>(requestState: RequestState<TData> | CacheRequestState<TData>, defaultData: TData): TData => {
    switch (requestState.code) {
        case StatusCode.COMPLETE:
            return requestState.data;
        case StatusCode.PENDING:
            if (isCacheRequestState<TData>(requestState)) {
                return requestState.data ?? defaultData;
            } else {
                return defaultData;
            }
        default:
            return defaultData;
    }
}

export const isCacheRequestState = <TData>(obj: any): obj is CacheRequestState<TData> => {
    return obj.data !== undefined;
}
